import { ArrowLeft } from "lucide-react";
import { COMPANY } from "@/lib/company";

export default function CookiePolicy() {
  const reset = () => {
    localStorage.removeItem("dea_cookie_consent_v1");
    window.location.reload();
  };

  return (
    <div
      data-testid="cookie-page"
      className="min-h-screen bg-[#050B14] text-slate-100"
    >
      <header className="border-b border-[#1E293B] py-5 px-6 md:px-10">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm"
        >
          <ArrowLeft size={16} /> Torna al sito
        </a>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-px bg-[#FF5A1F]" />
          <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
            Documento legale
          </span>
        </div>
        <h1 className="headline text-white text-4xl md:text-6xl">
          Cookie{" "}
          <span className="font-display italic font-medium text-slate-300">
            Policy
          </span>
        </h1>
        <p className="text-slate-500 text-sm mt-4">
          Ultimo aggiornamento: dicembre 2025
        </p>

        <div className="mt-12 space-y-8 text-slate-300 leading-relaxed text-[15px]">
          <section>
            <h2 className="text-white text-xl font-bold tracking-tight mb-3">
              Cosa sono i cookie
            </h2>
            <p>
              I cookie sono piccoli file di testo che i siti visitati salvano
              sul tuo dispositivo per memorizzare informazioni utili al
              funzionamento del sito o per finalità statistiche.
            </p>
          </section>

          <section>
            <h2 className="text-white text-xl font-bold tracking-tight mb-3">
              Cookie utilizzati da questo sito
            </h2>
            <p>
              Questo sito utilizza esclusivamente le tipologie di cookie qui
              elencate. Non utilizziamo cookie di profilazione né di
              marketing.
            </p>

            <div className="mt-6 border border-[#1E293B]">
              <div className="bg-[#0A1120] p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-white font-bold">
                    1. Cookie tecnici / necessari
                  </h3>
                  <span className="text-[10px] tracking-wider uppercase bg-slate-700 text-white px-2 py-0.5 font-bold">
                    Sempre attivi
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  Indispensabili per il corretto funzionamento del sito. Non
                  richiedono consenso.
                </p>
                <ul className="mt-3 text-xs text-slate-500 space-y-1">
                  <li>
                    <code className="text-slate-300">
                      dea_cookie_consent_v1
                    </code>{" "}
                    — memorizza le tue preferenze cookie. Durata: 12 mesi.
                  </li>
                  <li>
                    <code className="text-slate-300">dea_admin_token</code> —
                    sessione area riservata. Durata: 12 ore.
                  </li>
                </ul>
              </div>
              <div className="bg-[#0A1120] border-t border-[#1E293B] p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-white font-bold">2. Cookie analitici</h3>
                  <span className="text-[10px] tracking-wider uppercase bg-slate-700 text-white px-2 py-0.5 font-bold">
                    Opzionali
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  Al momento il sito non integra servizi di analitica. Quando
                  in futuro integreremo Google Analytics o strumenti simili, ti
                  verrà richiesto un consenso esplicito tramite il banner.
                </p>
              </div>
              <div className="bg-[#0A1120] border-t border-[#1E293B] p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-white font-bold">
                    3. Cookie di terze parti
                  </h3>
                  <span className="text-[10px] tracking-wider uppercase bg-slate-700 text-white px-2 py-0.5 font-bold">
                    Nessuno
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  Il sito carica caratteri tipografici da Google Fonts e
                  immagini ottimizzate da Unsplash. Tali servizi non rilasciano
                  cookie traccianti sul tuo dispositivo durante la
                  navigazione.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-white text-xl font-bold tracking-tight mb-3">
              Gestione delle preferenze
            </h2>
            <p>
              Puoi modificare le tue preferenze in qualsiasi momento cliccando
              il pulsante qui sotto:
            </p>
            <button
              data-testid="cookie-reset-btn"
              onClick={reset}
              className="mt-4 inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-5 py-3 text-sm font-semibold transition-colors"
            >
              Modifica preferenze cookie
            </button>
            <p className="mt-5 text-sm">
              Puoi inoltre bloccare o eliminare i cookie direttamente dal tuo
              browser. Per istruzioni specifiche consulta:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
              <li>
                <a
                  href="https://support.google.com/chrome/answer/95647"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF5A1F] underline"
                >
                  Google Chrome
                </a>
              </li>
              <li>
                <a
                  href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF5A1F] underline"
                >
                  Mozilla Firefox
                </a>
              </li>
              <li>
                <a
                  href="https://support.apple.com/it-it/guide/safari/sfri11471/mac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF5A1F] underline"
                >
                  Apple Safari
                </a>
              </li>
              <li>
                <a
                  href="https://support.microsoft.com/it-it/microsoft-edge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF5A1F] underline"
                >
                  Microsoft Edge
                </a>
              </li>
              <li>
                <a
                  href="https://support.brave.com/hc/en-us/articles/360050634931"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF5A1F] underline"
                >
                  Brave
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-xl font-bold tracking-tight mb-3">
              Titolare del trattamento
            </h2>
            <p>
              {COMPANY.name} — Sede operativa: {COMPANY.area}. Per qualsiasi
              richiesta:{" "}
              <a
                href={`mailto:${COMPANY.email}`}
                className="text-[#FF5A1F] underline"
              >
                {COMPANY.email}
              </a>
              .
            </p>
            <p className="mt-3">
              Maggiori informazioni nella{" "}
              <a
                href="/privacy-policy"
                className="text-[#FF5A1F] underline"
              >
                Privacy Policy
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-[#1E293B] py-8 px-6 md:px-10 text-xs text-slate-500">
        © {new Date().getFullYear()} {COMPANY.name} — Tutti i diritti riservati.
      </footer>
    </div>
  );
}
